import { type FormikHelpers, useFormikContext } from 'formik';
import { useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import type { ModalElement } from '@hivebrite/design-system/Dialog';
import { FakeLink } from '@hivebrite/design-system/Link';
import { Loader } from '@hivebrite/design-system/Loader';
import { EmptyStateWithoutCard } from '@hivebrite/design-system/SimpleCard';
import { Skeleton } from '@hivebrite/design-system/Skeleton';
import { Tooltip } from '@hivebrite/design-system/Tooltip';

import { useInfiniteScroll } from 'hb-react/shared/hooks/useInfiniteScroll';

import FilteredUser from './FilteredUser';
import type { FormValues, NotifyUser } from '../types';

const SCROLL_THRESHOLD = 370;
// This is the displacement of the scrollbar so that it doesn't overflow the checkboxes in the container
const DISPLACEMENT = 4;

interface UsersProps {
  clearFilters: (
    setFieldValue: FormikHelpers<FormValues>['setFieldValue'],
  ) => void;
  clearSelection: () => void;
  fetchNextPage: () => void;
  hasError: boolean;
  hasMorePages: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isMobile: boolean;
  maxTargetedUsers: number;
  selectAllUsers: () => void;
  selectUser: (user: NotifyUser) => void;
  selectedUserIds?: number[];
  removeUser: (user: NotifyUser) => void;
  users?: NotifyUser[];
  usersCount?: number;
  isSelectAllDisabled?: boolean;
  modalRef?: React.RefObject<ModalElement>;
}

const Users = ({
  clearFilters,
  clearSelection,
  fetchNextPage,
  hasError,
  hasMorePages,
  isFetchingNextPage,
  isLoading,
  isMobile,
  maxTargetedUsers,
  selectAllUsers,
  selectUser,
  selectedUserIds = [],
  removeUser,
  users = [],
  usersCount = 0,
  isSelectAllDisabled = false,
  modalRef,
}: UsersProps) => {
  const { setFieldValue } = useFormikContext();
  const scrollableList = useRef<HTMLDivElement>(null);

  const selectedUserIdsSet = useMemo(
    () => new Set(selectedUserIds),
    [selectedUserIds],
  );
  const areAllUsersSelected = useMemo(
    () =>
      selectedUserIdsSet.size >= users.length &&
      users.every((user) => selectedUserIdsSet.has(user.id)),
    [selectedUserIdsSet, users],
  );
  const isMaxUsersSelected = selectedUserIds.length >= maxTargetedUsers;

  const handleOnChange = (user) => {
    if (selectedUserIds.includes(user.id)) {
      removeUser(user);
    } else {
      selectUser(user);
    }
  };

  const handleOnClearFilters = () => {
    clearFilters(setFieldValue);
  };

  const loadMore = useCallback(() => {
    if (hasMorePages && !isFetchingNextPage && !hasError) {
      fetchNextPage();
    }
  }, [hasError, hasMorePages, isFetchingNextPage, fetchNextPage]);

  useInfiniteScroll(scrollableList?.current, loadMore, {
    distance: SCROLL_THRESHOLD,
    enabled: hasMorePages,
  });

  if (!isLoading && !users.length && !hasError) {
    return (
      <UsersWrapper $isMobile={isMobile} flexDirection="column" pb={4}>
        <EmptyStateWithoutCard
          title={s__('NotifyPeople|No users found')}
          description={s__('NotifyPeople|Try applying another filter search.')}
          buttonComponent={() => (
            <Button onClick={handleOnClearFilters}>
              {__('Reset filters')}
            </Button>
          )}
          faIconProps={{ iconName: 'telescope' }}
        />
      </UsersWrapper>
    );
  }

  const selectAllButton = () => {
    if (isLoading) {
      return null;
    }

    if (areAllUsersSelected) {
      return <FakeLink onClick={clearSelection}>{__('Unselect all')}</FakeLink>;
    }

    if (isSelectAllDisabled) {
      const tooltipMessage = s__(
        'NotifyPeople|This option is disabled because the number of users matching your current filters would exceed the selection limit. Please refine your search criteria to reduce your current selection.',
      );

      return (
        <Tooltip
          childrenElement={<TooltipMessage>{tooltipMessage}</TooltipMessage>}
          fixedParent
          inline
          maxWidth="350px"
          portalTarget={modalRef?.current?.getModalElement()}
        >
          <FakeLink isDisabled onClick={() => {}} aria-disabled="true">
            {__('Select all')}
          </FakeLink>
        </Tooltip>
      );
    }

    return <FakeLink onClick={selectAllUsers}>{__('Select all')}</FakeLink>;
  };

  return (
    <UsersWrapper
      $isMobile={isMobile}
      flexDirection="column"
      mr={-DISPLACEMENT}
    >
      <Box
        justifyContent="space-between"
        alignItems="center"
        mb={6}
        mr={DISPLACEMENT}
      >
        <Box font="h4-semibold">
          {__('Users (%{userCount})').i__({
            userCount: usersCount.toString(),
          })}
        </Box>
        {selectAllButton()}
      </Box>

      <Box
        flexDirection="column"
        overflow="auto"
        ref={scrollableList}
        pr={DISPLACEMENT}
      >
        {isLoading ? (
          <>
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} height={42} mb={3} />
            ))}
          </>
        ) : (
          <>
            {users.map((user) => {
              const isSelected = selectedUserIdsSet.has(user.id);
              const isDisabled = !isSelected && isMaxUsersSelected;

              return (
                <FilteredUser
                  isDisabled={isDisabled}
                  isSelected={isSelected}
                  key={user.id}
                  onUserClick={handleOnChange}
                  user={user}
                />
              );
            })}
            {hasError && (
              <Box mt={4} mb={4} textAlign="center">
                <Box mr={2}>
                  {s__('NotifyPeople|An error occurred while fetching users.')}
                </Box>
                <FakeLink onClick={fetchNextPage}>{__('Retry')}</FakeLink>
              </Box>
            )}
          </>
        )}
      </Box>
      {isFetchingNextPage && <Loader />}
    </UsersWrapper>
  );
};

export default Users;

interface UsersWrapperProps {
  $isMobile: boolean;
}

const UsersWrapper = styled(Box).attrs<UsersWrapperProps>({
  mb: { _: 0, m: 4 },
  overflow: 'auto',
  pt: 4,
})`
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: ${({ theme }) => theme.space[DISPLACEMENT]}px;
    height: 1px;
    background-color: ${({ theme }) => theme.colors.horizon};
  }

  &::before {
    top: 0;
  }

  &::after {
    bottom: 0;
    display: ${({ $isMobile }) => ($isMobile ? 'none' : 'block')};
  }
`;

const TooltipMessage = styled.span`
  text-align: left;
`;
